import {
  useUpdateCandidate,
  useUpdateCandidateStatus,
  type UpdateCandidateStatusInput,
} from "@vidario/api-client";
import {
  candidateMoves,
  moveBack,
  safeHttpUrl,
  scoreBgVar,
  scoreColorVar,
  statusActionsLocked,
  olderPreScore,
  olderPreScoreLine,
  vorauswahlLine,
  vorauswahlOf,
  type Candidate,
  type CandidateStatus,
  type Job,
  type SidebarMove,
} from "@vidario/domain";
import {
  Avatar,
  Button,
  Card,
  HiddenBlock,
  cn,
  DistanceChip,
  Input,
  Label,
  StatusBadge,
  SubScoreBars,
  useToast,
} from "@vidario/ui";
import {
  CheckCircle,
  ExternalLink,
  MessageSquare,
  Mic,
  Pencil,
  Send,
  Sparkles,
  Star,
  X,
} from "lucide-react";
import { showsReplacement, userInitials } from "@vidario/domain";
import { useRef, useState, type ReactElement, type CSSProperties } from "react";

import { ConfirmOpenButton, PROFILE_CONFIRM } from "../../anonym/ConfirmOpenButton";
import { CLASSES_NOTE, HIDDEN_TEXT } from "../../anonym/copy";
import { CandidateLocation } from "../../anonym/CandidateLocation";
import { useAnonymView, useMaskedCandidate } from "../../anonym/useAnonymView";
import { MoveBackToLonglist } from "./MoveBackToLonglist";

const MOVE_ACTIONS: Record<SidebarMove, { label: string; icon: ReactElement; done: string }> = {
  selected: {
    label: "In Ausgewählte Talente",
    icon: <Star size={14} />,
    done: "In Ausgewählte Talente verschoben.",
  },
  outreach: { label: "In Outreach senden", icon: <Send size={14} />, done: "Outreach gestartet." },
  prequal: {
    label: "Vorqualifizierung abgeschlossen",
    icon: <MessageSquare size={14} />,
    done: "Vorqualifizierung markiert.",
  },
  interview: {
    label: "Tiefeninterview abgeschlossen",
    icon: <Mic size={14} />,
    done: "Tiefeninterview markiert.",
  },
  recommendation: {
    label: "In Empfehlung aufnehmen",
    icon: <CheckCircle size={14} />,
    done: "In Empfehlung aufgenommen.",
  },
};

export interface ActionsSidebarProps {
  /** URL-derived jobId (string), used for cache invalidation. */
  jobId: string;
  candidate: Candidate;
  job: Job;
  isAdmin: boolean;
  onReject: () => void;
  /** Parent route's reject-mutation pending state. Threaded down so
   * the sidebar buttons disable while the Ablehnen-PATCH is in flight
   * (the dialog modal blocks clicks, but the buttons would otherwise
   * still register as enabled after the dialog closes and before the
   * candidate cache picks up status=rejected). */
  rejecting?: boolean;
}

/** MVP §4.1 + §4.8: left-rail card with identity, status badge,
 * Match-Score, sub-score bars, and role-aware status actions. */
export function ActionsSidebar({
  jobId,
  candidate: c,
  job,
  isAdmin,
  onReject,
  rejecting = false,
}: ActionsSidebarProps): ReactElement {
  const update = useUpdateCandidateStatus();
  const toast = useToast();
  const { candidate: shown, hidden } = useMaskedCandidate(c);
  // After a move back the button is gone; focus stays in the Aktionen block (#162).
  const actionsHeadingRef = useRef<HTMLDivElement>(null);

  const run = (
    input: Omit<UpdateCandidateStatusInput, "candidateId" | "jobId">,
    successMessage: string,
  ): void => {
    update.mutate(
      { jobId, candidateId: c.id, ...input },
      {
        onSuccess: () =>
          toast.push({ kind: "success", title: "Status aktualisiert", message: successMessage }),
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const setStatus = (status: CandidateStatus, msg: string): void => {
    run({ status }, msg);
  };

  // Only the moves the backend accepts from here, for this role (#22).
  // Rejected candidates get none: un-rejecting runs via the banner.
  const moves = candidateMoves(c.status, { isAdmin });
  // A sorted-out row's score is the Vorauswahl's or an older algorithmic one,
  // not the Vidario-Match (#135, #186).
  const vorauswahl = vorauswahlOf(c);
  const priorScore = olderPreScore(c);
  const busy = update.isPending || rejecting;

  return (
    <Card>
      <ProfileBlock jobId={jobId} candidate={c} job={job} isAdmin={isAdmin} />

      {vorauswahl && (
        <div
          data-testid="candidate-vorauswahl"
          className="border-b border-border-default py-4 text-[12px] text-text-secondary"
        >
          {vorauswahlLine(vorauswahl)}
        </div>
      )}

      {priorScore != null && (
        <div
          data-testid="candidate-prior-score"
          className="border-b border-border-default py-4 text-[12px] text-text-secondary"
        >
          {olderPreScoreLine(priorScore)}
        </div>
      )}

      {c.score != null && !vorauswahl && priorScore == null && (
        <div className="border-b border-border-default py-4">
          <div className="mb-2.5 flex items-baseline justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
              Vidario-Match
            </span>
            <span
              className="tabular-nums text-[26px] font-bold leading-none -tracking-[0.5px]"
              style={{ color: scoreColorVar(c.score) }}
            >
              {c.score}
            </span>
          </div>
          <div className="mb-4 h-1.5 overflow-hidden rounded-[3px] bg-neutral-100">
            <div
              aria-hidden="true"
              className="h-full"
              style={
                { width: `${c.score}%`, background: scoreBgVar(c.score) } satisfies CSSProperties
              }
            />
          </div>
          {Object.keys(c.subScores).length > 0 && <SubScoreBars sub={c.subScores} compact />}
          {(hasReasoning(shown.subReasoning) || (hidden?.reasoningAxes.length ?? 0) > 0) && (
            <details className="mt-3 text-[12px]">
              <summary className="cursor-pointer text-text-secondary hover:text-text-primary">
                Begründung anzeigen
              </summary>
              <SubScoreReasoning reasoning={shown.subReasoning} />
              {(hidden?.reasoningAxes.length ?? 0) > 0 && (
                <HiddenBlock className="mt-2 text-[12px]">{HIDDEN_TEXT}</HiddenBlock>
              )}
              {reasoningReplaced(c.subReasoning, shown.subReasoning) && (
                <p className="mt-2 mb-0 text-xs text-text-tertiary">{CLASSES_NOTE}</p>
              )}
            </details>
          )}
          <div className="mt-2.5 inline-flex items-start gap-1.5 text-[11px] leading-relaxed text-text-tertiary">
            <Sparkles size={10} className="mt-0.5" />
            Gewichtet: Anforderungen 40 %, Erfahrung 30 %, Branche 20 %, Standort 10 %.
          </div>
        </div>
      )}

      <div className="pt-4">
        <div
          ref={actionsHeadingRef}
          tabIndex={-1}
          className="mb-2.5 rounded-sm text-xs font-medium uppercase tracking-wider text-text-tertiary focus:outline-none"
        >
          Aktionen
        </div>
        {statusActionsLocked(c.status, job.status) ? (
          <p className="text-sm text-text-secondary">
            {job.status === "closed"
              ? "Die Stelle ist geschlossen. Kandidaten-Status lassen sich nicht mehr ändern."
              : "Vidario prüft diesen Kandidaten gerade. Aktionen sind danach wieder möglich."}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {moves.map((move) => {
              const action = MOVE_ACTIONS[move];
              return (
                <Button
                  key={move}
                  {...(move !== "recommendation" && { variant: "secondary" as const })}
                  size="sm"
                  leftIcon={action.icon}
                  disabled={busy}
                  onClick={() => setStatus(move, action.done)}
                >
                  {action.label}
                </Button>
              );
            })}
            {!isAdmin && moves.length === 0 && c.status !== "rejected" && (
              <p className="m-0 text-sm text-text-secondary">
                Die nächsten Schritte übernimmt das Vidario-Team.
              </p>
            )}
            <MoveBackToLonglist
              jobId={jobId}
              candidate={c}
              job={job}
              isAdmin={isAdmin}
              onMoved={() => actionsHeadingRef.current?.focus()}
              disabled={busy}
            />
            {moveBack(c.status, { isAdmin }) && (
              <hr className="my-1 border-0 border-t border-border-default" />
            )}
            {c.status !== "rejected" && (
              <Button
                variant="destructive-ghost"
                size="sm"
                leftIcon={<X size={14} />}
                onClick={onReject}
                disabled={update.isPending || rejecting}
              >
                Ablehnen
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function hasReasoning(reasoning: Candidate["subReasoning"]): boolean {
  return Object.values(reasoning).some((b) => b.length > 0);
}

// Compared axis by axis, so a withheld axis can't shift the lines of the next.
function reasoningReplaced(
  raw: Candidate["subReasoning"],
  shown: Candidate["subReasoning"],
): boolean {
  return Object.keys(raw).some((axis) => showsReplacement(raw[axis] ?? [], shown[axis] ?? []));
}

const REASONING_LABELS: Record<string, string> = {
  skills: "Anforderungen",
  experience: "Erfahrung",
  industry: "Branche",
  location: "Standort",
};

/** Collapsible per-axis reasoning bullets from the hybrid AI scorer
 * (Finding 6). Empty when the algorithmic fallback ran. */
function SubScoreReasoning({ reasoning }: { reasoning: Candidate["subReasoning"] }): ReactElement {
  return (
    <div className="mt-2 flex flex-col gap-2.5 text-text-secondary">
      {Object.entries(reasoning).map(([axis, bullets]) => {
        if (bullets.length === 0) return null;
        return (
          <div key={axis}>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              {REASONING_LABELS[axis] ?? axis}
            </div>
            <ul className="m-0 list-disc pl-4 leading-relaxed">
              {bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function ProfileBlock({
  jobId,
  candidate: c,
  job,
  isAdmin,
}: {
  jobId: string;
  candidate: Candidate;
  job: Job;
  isAdmin: boolean;
}): ReactElement {
  // Drafts start from the real candidate; only what's shown is masked.
  const anonym = useAnonymView();
  const { candidate: s } = useMaskedCandidate(c);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: c.name,
    currentTitle: c.currentTitle ?? "",
    currentCompany: c.currentCompany ?? "",
    location: c.location ?? "",
    linkedinUrl: c.linkedinUrl ?? "",
    xingUrl: c.xingUrl ?? "",
  });
  const update = useUpdateCandidate();
  const toast = useToast();

  const save = (): void => {
    if (!draft.name.trim()) {
      toast.push({ kind: "error", title: "Name fehlt", message: "Bitte einen Namen angeben." });
      return;
    }
    update.mutate(
      {
        jobId,
        candidateId: c.id,
        patch: {
          name: draft.name.trim(),
          currentTitle: draft.currentTitle || null,
          currentCompany: draft.currentCompany || null,
          location: draft.location || null,
          linkedinUrl: draft.linkedinUrl || null,
          xingUrl: draft.xingUrl || null,
        },
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast.push({
            kind: "success",
            title: "Profil gespeichert",
            message: "Profildaten aktualisiert.",
          });
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const cancel = (): void => {
    setDraft({
      name: c.name,
      currentTitle: c.currentTitle ?? "",
      currentCompany: c.currentCompany ?? "",
      location: c.location ?? "",
      linkedinUrl: c.linkedinUrl ?? "",
      xingUrl: c.xingUrl ?? "",
    });
    setEditing(false);
  };

  if (editing && anonym) {
    // The open form is kept and comes back when the Anonym-Ansicht is off.
    return (
      <div className="border-b border-border-default pb-4 text-center">
        <HiddenBlock>Bearbeiten ist in der Anonym-Ansicht gesperrt.</HiddenBlock>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-2.5 border-b border-border-default pb-4">
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">
            Profil bearbeiten
          </span>
        </div>
        <ProfileField id="prof-name" label="Name">
          <Input
            id="prof-name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </ProfileField>
        <ProfileField id="prof-title" label="Aktuelle Position">
          <Input
            id="prof-title"
            value={draft.currentTitle}
            onChange={(e) => setDraft({ ...draft, currentTitle: e.target.value })}
          />
        </ProfileField>
        <ProfileField id="prof-company" label="Aktuelles Unternehmen">
          <Input
            id="prof-company"
            value={draft.currentCompany}
            onChange={(e) => setDraft({ ...draft, currentCompany: e.target.value })}
          />
        </ProfileField>
        <ProfileField id="prof-location" label="Wohnort">
          <Input
            id="prof-location"
            value={draft.location}
            onChange={(e) => setDraft({ ...draft, location: e.target.value })}
          />
        </ProfileField>
        <ProfileField id="prof-linkedin" label="LinkedIn-URL">
          <Input
            id="prof-linkedin"
            type="url"
            value={draft.linkedinUrl}
            onChange={(e) => setDraft({ ...draft, linkedinUrl: e.target.value })}
            placeholder="https://www.linkedin.com/in/…"
          />
        </ProfileField>
        <ProfileField id="prof-xing" label="Xing-URL">
          <Input
            id="prof-xing"
            type="url"
            value={draft.xingUrl}
            onChange={(e) => setDraft({ ...draft, xingUrl: e.target.value })}
            placeholder="https://www.xing.com/profile/…"
          />
        </ProfileField>
        <div className="mt-1 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={cancel}>
            Abbrechen
          </Button>
          <Button size="sm" loading={update.isPending} onClick={save}>
            Speichern
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center border-b border-border-default pb-4 text-center">
      {isAdmin && job.status !== "closed" && !anonym && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="absolute right-0 top-0 inline-flex items-center gap-1 text-[12px] font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <Pencil size={12} /> Bearbeiten
        </button>
      )}
      <Avatar
        initials={anonym ? "" : userInitials(s.name)}
        variant={anonym ? "placeholder" : "initials"}
        size={80}
      />
      <h1
        className={cn(
          "mb-0.5 mt-3.5 text-xl font-semibold -tracking-[0.3px]",
          anonym && "font-mono",
        )}
      >
        {s.name}
      </h1>
      {/* Admins see the Candidate code, to find the person named in an Anonym-Ansicht (#140). */}
      {isAdmin && c.code && !anonym && (
        <div className="mb-1">
          <span
            className="inline-flex h-5 items-center rounded-sm bg-neutral-100 px-1.5 font-mono text-xs text-neutral-700"
            title="Kandidatencode"
          >
            {c.code}
          </span>
        </div>
      )}
      {s.currentTitle && <div className="text-[13px] text-text-secondary">{s.currentTitle}</div>}
      {s.currentCompany && <div className="text-[13px] text-text-tertiary">{s.currentCompany}</div>}
      <CandidateLocation
        className="mt-2"
        location={s.location}
        distanceKm={s.distanceKm}
        jobLocation={job.location}
        anonym={anonym}
        withDistance={false}
      />
      {(c.linkedinUrl || c.xingUrl) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
          {c.linkedinUrl &&
            (anonym ? (
              <ProfileConfirm
                candidateId={c.id}
                url={c.linkedinUrl}
                kind="linkedin"
                label="LinkedIn-Profil"
              />
            ) : (
              <ProfileLink href={c.linkedinUrl} label="LinkedIn-Profil" />
            ))}
          {c.xingUrl &&
            (anonym ? (
              <ProfileConfirm candidateId={c.id} url={c.xingUrl} kind="xing" label="Xing-Profil" />
            ) : (
              <ProfileLink href={c.xingUrl} label="Xing-Profil" />
            ))}
        </div>
      )}
      {c.distanceKm != null && (
        <div className="mt-2">
          <DistanceChip km={c.distanceKm} jobLocation={job.location} anonym={anonym} />
        </div>
      )}
      <div className="mt-3.5">
        <StatusBadge status={c.status} />
      </div>
    </div>
  );
}

function ProfileField({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactElement;
}): ReactElement {
  return (
    <div className="text-left">
      <Label htmlFor={id} className="mb-1 block text-[11px] font-medium text-text-tertiary">
        {label}
      </Label>
      {children}
    </div>
  );
}

const PROFILE_CHIP =
  "inline-flex cursor-pointer items-center gap-1 rounded-full border border-border-default bg-neutral-50 px-2 py-0.5 text-[11px] font-medium text-text-secondary transition-colors hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1";

/** The profile chip in the Anonym-Ansicht (#140): opens only after a confirm. */
function ProfileConfirm({
  candidateId,
  url,
  kind,
  label,
}: {
  candidateId: string;
  url: string;
  kind: "linkedin" | "xing";
  label: string;
}): ReactElement {
  return (
    <ConfirmOpenButton
      url={url}
      candidateId={candidateId}
      action="source_profile_opened"
      kind={kind}
      className={PROFILE_CHIP}
      {...PROFILE_CONFIRM}
    >
      <ExternalLink size={11} aria-hidden="true" />
      {label}
    </ConfirmOpenButton>
  );
}

/** Chip-style link to a candidate's external profile (Xing / LinkedIn).
 * Opens the source profile in a new tab — used in the candidate detail
 * header so the recruiter can verify the underlying profile. The URL is
 * scraped external data, so it's run through `safeHttpUrl`; an unsafe /
 * unparseable value renders as a non-clickable chip (React does not
 * sanitise href). */
function ProfileLink({ href, label }: { href: string; label: string }): ReactElement {
  const safe = safeHttpUrl(href);
  if (!safe) {
    return (
      <span
        title="Ungültige Profil-URL"
        className="inline-flex items-center gap-1 rounded-full border border-border-default bg-neutral-50 px-2 py-0.5 text-[11px] font-medium text-text-tertiary"
      >
        <ExternalLink size={11} />
        {label}
      </span>
    );
  }
  return (
    <a
      href={safe}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded-full border border-border-default bg-neutral-50 px-2 py-0.5 text-[11px] font-medium text-text-secondary transition-colors hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
    >
      <ExternalLink size={11} />
      {label}
    </a>
  );
}
